/**
 * StatusCard
 *
 * 通訳モードの中央パネル。Camera と同じ 4:5 比率の枠を持ち、
 * 状態に応じて中身（アイコン、タイトル、サブテキスト）を切り替える。
 *
 * 4状態:
 *   - idle:        🎤 / 待機中 / タップして開始
 *   - listening:   🎤 (active) + LIVE バッジ + 認識中…/ スキャンライン
 *   - translating: スピナー / 翻訳中…
 *   - error:       ⚠️ / エラー文 / RETRYボタンで idle へ戻す（外側で実装）
 */

import type { InterpretStatus } from '../types';

import styles from './StatusCard.module.css';

export interface StatusCardProps {
  status: InterpretStatus;
  /** error 時のメッセージ */
  errorMessage?: string | null;
}

export function StatusCard({ status, errorMessage }: StatusCardProps) {
  const isError = status === 'error';

  return (
    <div className={styles.root} role="status" aria-live="polite">
      {/* 背景ティント */}
      <div className={isError ? styles.tintError : styles.tint} aria-hidden />

      {/* listening 中はスキャンラインを表示 */}
      {status === 'listening' && (
        <div className={styles.scanLine} aria-hidden>
          <div className={styles.scanLineBar} />
        </div>
      )}

      {/* 4隅のコーナー */}
      <span className={`${styles.bracket} ${styles.bracketTopLeft}`} aria-hidden />
      <span className={`${styles.bracket} ${styles.bracketTopRight}`} aria-hidden />
      <span className={`${styles.bracket} ${styles.bracketBottomLeft}`} aria-hidden />
      <span className={`${styles.bracket} ${styles.bracketBottomRight}`} aria-hidden />

      {/* listening 中の LIVE バッジ */}
      {status === 'listening' && (
        <div className={styles.liveBadge} aria-hidden>
          <span className={styles.liveBadgeDot} />
          <span>LIVE</span>
        </div>
      )}

      {/* メインコンテンツ */}
      <div className={styles.content}>
        {status === 'idle' && (
          <>
            <span className={styles.icon} aria-hidden>🎤</span>
            <h2 className={styles.title}>STAND BY</h2>
            <p className={styles.subtitle}>下のSTARTボタンで通訳を開始</p>
          </>
        )}

        {status === 'listening' && (
          <>
            <span className={styles.icon} aria-hidden>🎤</span>
            <h2 className={styles.title}>LISTENING</h2>
            <p className={styles.subtitle}>英語を話しかけてください…</p>
          </>
        )}

        {status === 'translating' && (
          <>
            <span className={styles.spinner} aria-hidden />
            <h2 className={styles.title}>TRANSLATING</h2>
            <p className={styles.subtitle}>翻訳中…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <span className={`${styles.icon} ${styles.iconError}`} aria-hidden>⚠️</span>
            <h2 className={`${styles.title} ${styles.titleError}`}>ERROR</h2>
            <p className={`${styles.subtitle} ${styles.subtitleError}`}>
              {errorMessage ?? '通訳セッションでエラーが発生しました。'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
